import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

/**
 * PlantContext - Manages plant-related state for the Plant Tab
 *
 * Features:
 * - Favorites management (localStorage persistence in Phase 1)
 * - Catalog dialog state (open/close)
 * - Filter state for plant search
 * - Placement mode for drag-drop and click-to-place
 * - Selected plant tracking for placement
 *
 * State Shape:
 * {
 *   favorites: number[],                    // Array of plant IDs
 *   catalogOpen: boolean,                   // Catalog dialog visibility
 *   selectedFilters: {                      // Active filter state
 *     sunlight: string[],
 *     waterRequirement: string[],
 *     toxicToPets: boolean | null,
 *     toxicToChildren: boolean | null,
 *     beeF

riendly: boolean | null,
 *     containerSuitable: boolean | null
 *   },
 *   placementMode: boolean,                 // Click-to-place mode active
 *   selectedPlantForPlacement: object | null // Plant being placed
 * }
 */

const PlantContext = createContext(null);

// localStorage key for favorites persistence (Phase 1)
const FAVORITES_STORAGE_KEY = 'gartenplaner_favorites';

// Initial filter state
const initialFilters = {
  sunlight: [],
  waterRequirement: [],
  toxicToPets: null,
  toxicToChildren: null,
  beeFriendly: null,
  containerSuitable: null,
};

/**
 * PlantProvider Component
 *
 * Provides plant-related state and actions to all child components.
 * Handles localStorage persistence for favorites in Phase 1.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
export function PlantProvider({ children }) {
  // Initialize favorites from localStorage
  const [favorites, setFavorites] = useState(() => {
    try {
      const stored = localStorage.getItem(FAVORITES_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);

        // Validate that parsed data is an array of numbers
        if (Array.isArray(parsed) && parsed.every(id => typeof id === 'number')) {
          return parsed;
        } else {
          console.warn('Invalid favorites data in localStorage, using empty array');
          return [];
        }
      }
    } catch (error) {
      console.error('Failed to load favorites from localStorage:', error);
      return [];
    }
    return [];
  });

  const [catalogOpen, setCatalogOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState(initialFilters);
  const [placementMode, setPlacementMode] = useState(false);
  const [selectedPlantForPlacement, setSelectedPlantForPlacement] = useState(null);

  // #764: remember where the active placement was started so an ESC-cancel can
  // route the user back into the catalog (with their filters) — but only when the
  // placement actually came from the catalog. Other entry points (favorites, plant
  // properties tab) leave the origin null and cancel silently, as before.
  // A ref (not state) because the cancel handler only reads it and nothing renders
  // from it — this also keeps cancelPlacementMode reference-stable.
  const placementOriginRef = useRef(null); // 'catalog' | null
  // Snapshot of the catalog's filter state to restore on reopen after cancel.
  const [catalogReturnState, setCatalogReturnState] = useState(null);
  // Bumped whenever a catalog-initiated placement is cancelled — MainLayout
  // watches this nonce and reopens the catalog seeded with catalogReturnState.
  const [catalogReopenNonce, setCatalogReopenNonce] = useState(0);

  /**
   * Save favorites to localStorage whenever they change
   * Handles quota exceeded errors gracefully
   */
  useEffect(() => {
    try {
      localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(favorites));
    } catch (error) {
      if (error.name === 'QuotaExceededError') {
        console.error('localStorage quota exceeded. Cannot save favorites.');
      } else {
        console.error('Failed to save favorites to localStorage:', error);
      }
    }
  }, [favorites]);

  /**
   * Add a plant to favorites
   * Prevents duplicate entries
   *
   * @param {number} plantId - ID of the plant to add
   */
  const addToFavorites = useCallback((plantId) => {
    if (typeof plantId !== 'number') {
      console.error('addToFavorites: plantId must be a number');
      return;
    }

    setFavorites(prev => {
      // Prevent duplicates
      if (prev.includes(plantId)) {
        return prev;
      }
      return [...prev, plantId];
    });
  }, []);

  /**
   * Remove a plant from favorites
   *
   * @param {number} plantId - ID of the plant to remove
   */
  const removeFromFavorites = useCallback((plantId) => {
    if (typeof plantId !== 'number') {
      console.error('removeFromFavorites: plantId must be a number');
      return;
    }

    setFavorites(prev => prev.filter(id => id !== plantId));
  }, []);

  /**
   * Replace all favorites with a new array (used for backend sync)
   *
   * @param {number[]} ids - Array of plant IDs
   */
  const setAllFavorites = useCallback((ids) => {
    if (!Array.isArray(ids) || !ids.every(id => typeof id === 'number')) {
      console.error('setAllFavorites: ids must be an array of numbers');
      return;
    }
    setFavorites(ids);
  }, []);

  /**
   * Check if a plant is in favorites
   *
   * @param {number} plantId - ID of the plant to check
   * @returns {boolean} True if plant is favorited
   */
  const isFavorite = useCallback((plantId) => {
    return favorites.includes(plantId);
  }, [favorites]);

  /**
   * Open the plant catalog dialog
   */
  const openCatalog = useCallback(() => {
    setCatalogOpen(true);
  }, []);

  /**
   * Close the plant catalog dialog
   */
  const closeCatalog = useCallback(() => {
    setCatalogOpen(false);
  }, []);

  /**
   * Update active filters
   * Merges new filters with existing state
   *
   * @param {Object} filters - Filter updates (partial or complete)
   */
  const setFilters = useCallback((filters) => {
    setSelectedFilters(prev => ({
      ...prev,
      ...filters,
    }));
  }, []);

  /**
   * Reset all filters to initial state
   */
  const clearFilters = useCallback(() => {
    setSelectedFilters(initialFilters);
  }, []);

  /**
   * Enter placement mode for a specific plant
   * Closes catalog automatically
   *
   * @param {Object} plant - Full plant object with all properties
   */
  const enterPlacementMode = useCallback((plant) => {
    if (!plant || typeof plant.id !== 'number') {
      console.error('enterPlacementMode: Invalid plant object');
      return;
    }

    // Non-catalog entry point (favorites, properties tab). Reset the origin so a
    // later ESC doesn't reopen the catalog (#764).
    placementOriginRef.current = null;
    setSelectedPlantForPlacement(plant);
    setPlacementMode(true);
    setCatalogOpen(false); // Auto-close catalog when entering placement mode
  }, []);

  /**
   * Tag the active placement as catalog-initiated and remember the catalog's
   * filter state, so an ESC-cancel can reopen the catalog exactly where the user
   * left off (#764). Called by PlantCatalogDialog right after enterPlacementMode.
   *
   * @param {Object|null} returnState - { filters, searchQuery, sortBy } to restore
   */
  const markPlacementFromCatalog = useCallback((returnState = null) => {
    placementOriginRef.current = 'catalog';
    setCatalogReturnState(returnState);
  }, []);

  /**
   * Exit placement mode after a completed placement (or a deliberate tab change).
   * Clears selected plant. Does NOT reopen the catalog.
   */
  const exitPlacementMode = useCallback(() => {
    placementOriginRef.current = null;
    setPlacementMode(false);
    setSelectedPlantForPlacement(null);
  }, []);

  /**
   * Cancel placement mode (ESC). Behaves like exitPlacementMode, but if the
   * placement came from the catalog it signals MainLayout to reopen the catalog
   * with the preserved filters (#764).
   */
  const cancelPlacementMode = useCallback(() => {
    const fromCatalog = placementOriginRef.current === 'catalog';
    placementOriginRef.current = null;
    setPlacementMode(false);
    setSelectedPlantForPlacement(null);
    if (fromCatalog) {
      setCatalogReopenNonce((n) => n + 1);
    }
  }, []);

  // Context value with all state and actions
  const value = {
    // State
    favorites,
    catalogOpen,
    selectedFilters,
    placementMode,
    selectedPlantForPlacement,
    catalogReturnState,
    catalogReopenNonce,

    // Actions
    addToFavorites,
    removeFromFavorites,
    setAllFavorites,
    isFavorite,
    openCatalog,
    closeCatalog,
    setFilters,
    clearFilters,
    enterPlacementMode,
    markPlacementFromCatalog,
    exitPlacementMode,
    cancelPlacementMode,
  };

  return (
    <PlantContext.Provider value={value}>
      {children}
    </PlantContext.Provider>
  );
}

/**
 * Hook to use PlantContext
 *
 * @returns {Object} Plant context value with state and actions
 * @throws {Error} If used outside of PlantProvider
 *
 * @example
 * function MyComponent() {
 *   const { favorites, addToFavorites, openCatalog } = usePlant();
 *
 *   return (
 *     <button onClick={() => addToFavorites(123)}>
 *       Add to Favorites
 *     </button>
 *   );
 * }
 */
export function usePlant() {
  const context = useContext(PlantContext);

  if (!context) {
    throw new Error('usePlant must be used within a PlantProvider');
  }

  return context;
}

export default PlantContext;
